import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(cookieConsent);
      setPreferences(savedPreferences);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      functional: false,
      analytics: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setPreferences(necessaryOnly);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Necessary cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-2xl">
        <div className="container mx-auto px-4 py-6">
          {!showSettings ? (
            // Simple Banner View
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex items-start space-x-4 flex-1">
                <Cookie className="w-8 h-8 text-[#8B1538] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Wir verwenden Cookies
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Wir verwenden Cookies, um Ihnen die bestmögliche Nutzung unserer Website zu ermöglichen. 
                    Notwendige Cookies sind für den Betrieb der Website erforderlich. 
                    Weitere Informationen finden Sie in unserer{' '}
                    <a href="/datenschutz" className="text-[#8B1538] hover:underline font-semibold">
                      Datenschutzerklärung
                    </a>.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-[#8B1538] hover:text-[#8B1538] whitespace-nowrap"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Einstellungen
                </Button>
                <Button
                  onClick={handleAcceptNecessary}
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-[#8B1538] hover:text-[#8B1538] whitespace-nowrap"
                >
                  Nur Notwendige
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="bg-[#8B1538] hover:bg-[#6B0F2A] text-white whitespace-nowrap"
                >
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          ) : (
            // Detailed Settings View
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Cookie-Einstellungen</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                        Notwendige Cookies
                        <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">Immer aktiv</span>
                      </h4>
                      <p className="text-sm text-gray-600">
                        Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                      </p>
                    </div>
                    <div className="ml-4">
                      <div className="w-12 h-6 bg-[#8B1538] rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-2">Funktionale Cookies</h4>
                      <p className="text-sm text-gray-600">
                        Diese Cookies ermöglichen erweiterte Funktionalitäten und Personalisierung.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('functional')}
                      className="ml-4"
                    >
                      <div className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.functional ? 'bg-[#8B1538] justify-end' : 'bg-gray-300 justify-start'
                      } px-1`}>
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-2">Analyse Cookies</h4>
                      <p className="text-sm text-gray-600">
                        Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className="ml-4"
                    >
                      <div className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.analytics ? 'bg-[#8B1538] justify-end' : 'bg-gray-300 justify-start'
                      } px-1`}>
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Button
                  onClick={handleAcceptNecessary}
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-[#8B1538] hover:text-[#8B1538]"
                >
                  Nur Notwendige
                </Button>
                <Button
                  onClick={handleSavePreferences}
                  className="bg-[#8B1538] hover:bg-[#6B0F2A] text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Auswahl speichern
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};